import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ShieldCheck, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { api } from '../lib/api';
import { toast } from 'sonner';

/**
 * Módulo SCME de Consentimientos (GDPR) — reutilizable en todo el ecosistema.
 * - Banner inicial: Aceptar todo / Rechazar opcionales / Configurar preferencias
 * - Preferencias por categoría (Listados de Valores) con bloqueo automático de servicios
 * - Registro en MongoDB con snapshot legal (misma filosofía que pedidos)
 * - Acceso permanente vía useConsent().openPreferences()
 */
const ConsentContext = createContext(null);
const LS_KEY = 'natur_consent';

export function useConsent() {
  return useContext(ConsentContext);
}

export function ConsentProvider({ children }) {
  const [data, setData] = useState(null); // { config, categories, services }
  const [stored, setStored] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch { return null; }
  });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [bannerSel, setBannerSel] = useState({});

  useEffect(() => {
    api.getConsentConfig().then(setData).catch(() => {});
  }, []);

  const categories = data?.categories || [];
  const services = data?.services || [];
  const config = data?.config || {};

  const hasConsent = (categoryId) => categoryId === 'necesarias' || !!stored?.preferences?.[categoryId];

  const persist = async (preferences) => {
    try {
      const record = await api.registerConsent({ preferences, platform: 'naturautoescuela.es' });
      const local = { consentId: record.id, preferences: record.preferences, date: record.createdAt, legalSnapshot: record.legalSnapshot };
      localStorage.setItem(LS_KEY, JSON.stringify(local));
      setStored(local);
      setPrefsOpen(false);
      toast.success('Preferencias de privacidad guardadas');
      const rejectedSome = categories.some((c) => c.id !== 'necesarias' && !preferences[c.id]);
      if (rejectedSome) {
        toast.info('Al rechazar algunas categorías, ciertas funcionalidades podrían no estar disponibles. Puede modificar sus preferencias en cualquier momento desde "Configuración de Privacidad" en el pie de página.', { duration: 8000 });
      }
    } catch {
      toast.error('No se pudieron guardar las preferencias');
    }
  };

  const acceptAll = () => persist(Object.fromEntries(categories.map((c) => [c.id, true])));
  const rejectOptional = () => persist(Object.fromEntries(categories.map((c) => [c.id, c.id === 'necesarias'])));
  const openPreferences = () => {
    setDraft(stored?.preferences || Object.fromEntries(categories.map((c) => [c.id, c.id === 'necesarias'])));
    setPrefsOpen(true);
  };

  const { pathname } = useLocation();
  const showBanner = data && config.active !== false && !stored && !pathname.startsWith('/admin');

  return (
    <ConsentContext.Provider value={{ hasConsent, openPreferences, stored }}>
      {children}

      {/* Banner inicial (estilo Real Cookie Banner: modal centrado con categorías) */}
      {showBanner && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="consent-banner">
          <div className="max-w-xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden my-auto">
            <div className="px-7 pt-6 pb-3">
              <div className="flex items-center gap-2.5 mb-2">
                <ShieldCheck className="w-6 h-6 text-teal-500 shrink-0" />
                <h2 className="font-black text-lg text-stone-800">{config.bannerTitle}</h2>
              </div>
              <p className="text-sm text-stone-500 leading-relaxed">{config.bannerText}</p>
            </div>
            <div className="px-7 pb-4 flex flex-wrap gap-x-5 gap-y-2" data-testid="consent-banner-categories">
              {categories.map((c) => {
                const locked = c.id === 'necesarias';
                return (
                  <label key={c.id} className={`flex items-center gap-2 text-sm font-bold ${locked ? 'text-stone-400' : 'text-stone-700 cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={locked ? true : !!bannerSel[c.id]}
                      disabled={locked}
                      onChange={(e) => setBannerSel({ ...bannerSel, [c.id]: e.target.checked })}
                      className="w-4 h-4 accent-amber-500"
                      data-testid={`consent-banner-cat-${c.id}`}
                    />
                    {c.label}
                  </label>
                );
              })}
            </div>
            <div className="px-7 pb-5 space-y-2.5">
              <button onClick={acceptAll} className="w-full py-3.5 rounded-lg font-black text-base bg-amber-400 hover:bg-amber-500 text-stone-900 transition-colors" data-testid="consent-accept-button">
                {config.acceptAllLabel || 'Aceptar todo'}
              </button>
              {categories.some((c) => c.id !== 'necesarias' && bannerSel[c.id]) && (
                <button onClick={() => persist({ ...Object.fromEntries(categories.map((c) => [c.id, !!bannerSel[c.id]])), necesarias: true })} className="w-full py-2.5 rounded-lg font-bold text-sm border-2 border-amber-400 text-amber-600 hover:bg-amber-50 transition-colors" data-testid="consent-save-selection">
                  Guardar selección
                </button>
              )}
              <div className="flex items-center justify-center gap-6 pt-1">
                <button onClick={rejectOptional} className="text-xs font-bold text-stone-500 hover:text-stone-800 underline transition-colors" data-testid="consent-reject-button">
                  Continuar sin consentimiento
                </button>
                <button onClick={openPreferences} className="text-xs font-bold text-stone-500 hover:text-stone-800 underline transition-colors" data-testid="consent-preferences-button">
                  {config.preferencesLabel || 'Ajustes individuales'}
                </button>
              </div>
            </div>
            <div className="bg-stone-50 border-t border-stone-100 px-7 py-3 flex flex-wrap justify-center gap-x-5 gap-y-1">
              <Link to="/legal/politica-cookies" className="text-[11px] text-stone-400 hover:text-stone-700 transition-colors">Política de Cookies</Link>
              <Link to="/legal/aviso-legal" className="text-[11px] text-stone-400 hover:text-stone-700 transition-colors">Aviso Legal</Link>
              <Link to="/legal" className="text-[11px] text-stone-400 hover:text-stone-700 transition-colors">Área Legal</Link>
            </div>
          </div>
        </div>
      )}

      {/* Preferencias avanzadas */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="consent-preferences-modal">
          <DialogHeader>
            <DialogTitle>Configuración de Privacidad</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {categories.map((c) => {
              const catServices = services.filter((s) => s.category === c.id);
              const locked = c.id === 'necesarias';
              return (
                <div key={c.id} className="border border-stone-200 rounded-lg p-4" data-testid={`consent-category-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-stone-800 text-sm">{c.label}{locked && <span className="ml-2 text-[10px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Siempre activas</span>}</span>
                    <Switch
                      checked={locked ? true : !!draft[c.id]}
                      disabled={locked}
                      onCheckedChange={(v) => setDraft({ ...draft, [c.id]: v })}
                      data-testid={`consent-switch-${c.id}`}
                    />
                  </div>
                  <p className="text-xs text-stone-500 leading-relaxed">{c.description}</p>
                  {catServices.length > 0 && (
                    <p className="text-[11px] text-stone-400 mt-2">Servicios: {catServices.map((s) => s.name).join(', ')}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mensaje informativo de consentimiento */}
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 flex gap-3" data-testid="consent-info-message">
            <Info className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
            <p className="text-xs text-stone-600 leading-relaxed whitespace-pre-line">{config.infoMessage}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-2">
            <button onClick={rejectOptional} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 border border-stone-300 hover:border-stone-500 transition-colors" data-testid="consent-modal-reject">
              Rechazar opcionales
            </button>
            <button onClick={() => persist(draft)} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="consent-save-preferences">
              Guardar preferencias
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </ConsentContext.Provider>
  );
}
